/**
 * Sadakat Puanı Sistemi — Saf Hesaplama Fonksiyonları
 *
 * %100 pure, deterministik fonksiyonlar.
 * Firestore, React, i18n veya Zustand bağımlılığı yoktur ve olamaz.
 * Bu dosya birden fazla feature tarafından tüketilir:
 *   features/loyalty, features/packages, features/commissions
 *
 * Firestore operasyonları için: features/loyalty/services/loyaltyService.js
 * Atomik transaction operasyonları için: features/loyalty/services/loyaltyTransactionService.js
 */

// ============================================================
// CONSTANTS
// ============================================================

export const TX_TYPES = Object.freeze({
  EARN: 'earn',
  REDEEM: 'redeem',
  EXPIRE: 'expire',
  REVERSE: 'reverse',
  ADJUST: 'adjust',
});

export const DEFAULT_LOYALTY_CONFIG = Object.freeze({
  earnRate: 0.1,
  redeemRate: 1,
  minRedeem: 100,
  expirationMonths: 12,
  tiers: [
    { name: 'bronze',   minLifetime: 0,     multiplier: 1.0, color: '#CD7F32' },
    { name: 'silver',   minLifetime: 1000,  multiplier: 1.2, color: '#C0C0C0' },
    { name: 'gold',     minLifetime: 5000,  multiplier: 1.5, color: '#FFD700' },
    { name: 'platinum', minLifetime: 15000, multiplier: 2.0, color: '#7F52CC' },
  ],
});

export const getDefaultLoyalty = () => ({
  currentPoints: 0,
  lifetimePoints: 0,
  tier: 'bronze',
  lastCalculatedAt: null,
  lastExpiryCheckAt: null,
  backfillCompletedAt: null,
  backfillBillCount: null,
});

// ============================================================
// PURE FUNCTIONS
// ============================================================

export const calculateTier = (lifetimePoints, config = DEFAULT_LOYALTY_CONFIG) => {
  if (!config || !Array.isArray(config.tiers) || config.tiers.length === 0) {
    return 'bronze';
  }
  const safeLifetime = Math.max(0, Number(lifetimePoints) || 0);
  const sorted = [...config.tiers].sort((a, b) => b.minLifetime - a.minLifetime);
  const match = sorted.find((t) => safeLifetime >= (Number(t.minLifetime) || 0));
  return match?.name || sorted[sorted.length - 1].name;
};

export const getTierConfig = (tierName, config = DEFAULT_LOYALTY_CONFIG) => {
  if (!config || !Array.isArray(config.tiers) || config.tiers.length === 0) {
    return { name: 'bronze', minLifetime: 0, multiplier: 1, color: '#CD7F32' };
  }
  const found = config.tiers.find((t) => t.name === tierName);
  if (found) return found;
  return [...config.tiers].sort((a, b) => a.minLifetime - b.minLifetime)[0];
};

export const calculateEarnPoints = (totalPrice, tierName, config = DEFAULT_LOYALTY_CONFIG) => {
  const price = Number(totalPrice) || 0;
  if (price <= 0) return 0;
  const rate = Number(config?.earnRate) || 0;
  if (rate <= 0) return 0;
  const tier = getTierConfig(tierName, config);
  const multiplier = Number(tier?.multiplier) || 1;
  return Math.floor(price * rate * multiplier);
};

export const pointsToDiscount = (points, config = DEFAULT_LOYALTY_CONFIG) => {
  const safePoints = Math.max(0, Math.floor(Number(points) || 0));
  const rate = Number(config?.redeemRate) || 1;
  return safePoints * rate;
};

export const discountToPoints = (amount, config = DEFAULT_LOYALTY_CONFIG) => {
  const safeAmount = Math.max(0, Number(amount) || 0);
  const rate = Number(config?.redeemRate) || 1;
  if (rate === 0) return 0;
  return Math.ceil(safeAmount / rate);
};

export const getNextTierProgress = (lifetimePoints, config = DEFAULT_LOYALTY_CONFIG) => {
  if (!config || !Array.isArray(config.tiers)) return null;
  const safeLifetime = Math.max(0, Number(lifetimePoints) || 0);
  const sorted = [...config.tiers].sort((a, b) => a.minLifetime - b.minLifetime);
  const next = sorted.find((t) => (Number(t.minLifetime) || 0) > safeLifetime);
  if (!next) return null;
  return {
    nextTier: next.name,
    pointsToGo: Math.max(0, Number(next.minLifetime) - safeLifetime),
  };
};

export const validateLoyaltyConfig = (config) => {
  const errors = [];
  if (!config || typeof config !== 'object') {
    return { isValid: false, errors: ['Config is null or not an object'] };
  }
  if (typeof config.earnRate !== 'number' || config.earnRate < 0) {
    errors.push('earnRate must be a non-negative number');
  }
  if (typeof config.redeemRate !== 'number' || config.redeemRate < 0) {
    errors.push('redeemRate must be a non-negative number');
  }
  if (typeof config.minRedeem !== 'number' || config.minRedeem < 0) {
    errors.push('minRedeem must be a non-negative number');
  }
  if (typeof config.expirationMonths !== 'number' || config.expirationMonths <= 0) {
    errors.push('expirationMonths must be a positive number');
  }
  if (!Array.isArray(config.tiers) || config.tiers.length === 0) {
    errors.push('tiers must be a non-empty array');
  } else {
    let prevMin = -Infinity;
    config.tiers.forEach((tier, i) => {
      if (!tier || typeof tier !== 'object') {
        errors.push(`tiers[${i}] must be an object`);
        return;
      }
      if (typeof tier.name !== 'string' || tier.name.trim() === '') {
        errors.push(`tiers[${i}].name must be a non-empty string`);
      }
      if (typeof tier.minLifetime !== 'number' || tier.minLifetime < 0) {
        errors.push(`tiers[${i}].minLifetime must be a non-negative number`);
      }
      if (typeof tier.multiplier !== 'number' || tier.multiplier <= 0) {
        errors.push(`tiers[${i}].multiplier must be a positive number`);
      }
      if (typeof tier.minLifetime === 'number' && tier.minLifetime < prevMin) {
        errors.push(`tiers[${i}].minLifetime must be >= previous tier's minLifetime`);
      }
      if (typeof tier.minLifetime === 'number') {
        prevMin = tier.minLifetime;
      }
    });
  }
  return { isValid: errors.length === 0, errors };
};

export const addMonthsToDate = (date, months) => {
  const result = new Date(date.getTime());
  const originalDay = result.getDate();
  result.setMonth(result.getMonth() + Number(months));
  if (result.getDate() < originalDay) {
    result.setDate(0);
  }
  return result;
};

export const normalizeLoyalty = (loyalty) => {
  const defaults = getDefaultLoyalty();
  if (!loyalty || typeof loyalty !== 'object') return defaults;
  return {
    ...defaults,
    ...loyalty,
    currentPoints: Math.max(0, Math.floor(Number(loyalty.currentPoints) || 0)),
    lifetimePoints: Math.max(0, Math.floor(Number(loyalty.lifetimePoints) || 0)),
    tier: typeof loyalty.tier === 'string' ? loyalty.tier : defaults.tier,
  };
};
